import { alpha, FormControl, FormHelperText, Grid, InputLabel, MenuItem, OutlinedInput, Select } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { divisionAutocolnSortedItemsTransportSelector } from 'app/selectors/transport';
import { useStateRef } from 'app/services';
import React, { forwardRef, useEffect, useImperativeHandle, useLayoutEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import InputMask from 'react-input-mask';
import { useSelector } from 'react-redux';

const DriverForm = forwardRef((props, ref) => {
  const theme = useTheme();
  const sortItemsDivisions = useSelector(divisionAutocolnSortedItemsTransportSelector);
  const [firstUpdate, setFirstUpdate, firstUpdateRef] = useStateRef(!!defaultValues);
  const { defaultValues, onSubmit } = props;
  const { control, reset, register, formState, handleSubmit } = useForm({
    defaultValues: {
      name: defaultValues ? defaultValues.name : '',
      number: defaultValues ? defaultValues.number : '',
      unit: defaultValues ? defaultValues.unit : '',
      id_autocolon: defaultValues ? defaultValues.id_autocolon : '',
      tabel_number: defaultValues ? defaultValues.tabel_number : '',
      external_id: defaultValues ? (defaultValues?.external_id ?? '') : '',
      external_groupid: defaultValues ? (defaultValues?.external_groupid ?? '') : '',
      drivinglicencenumber: defaultValues ? defaultValues.drivinglicencenumber : '',
    },
  });
  useImperativeHandle(ref, () => ({
    handleFormSubmit: () => {
      handleSubmit((data) => onSubmit(data))();
    },
  }));

  useLayoutEffect(() => {
    if (firstUpdateRef.current !== undefined) {
      setTimeout(() => setFirstUpdate(undefined), theme.transitions.duration.shorter);
    }
  }, [firstUpdateRef.current]);

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [defaultValues]);

  return (
    <form>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl error={!!formState.errors.name} margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>ФИО</InputLabel>
            <OutlinedInput
              label='ФИО'
              variant='outlined'
              notched={firstUpdate}
              fullWidth
              placeholder='Фамилия Имя Отчество'
              {...register('name', {
                pattern: /^([А-Яа-я\s]{3,255})$/iu,
                required: true,
                minLength: 3,
              })}></OutlinedInput>
            <FormHelperText>Используйте в ФИО только буквы кириллицы</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl error={!!formState.errors.number} margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Номер карты</InputLabel>
            <Controller
              name='number'
              control={control}
              render={({ field }) => (
                <InputMask
                  mask='RUD9999999999999'
                  maskChar=''
                  value={field.value}
                  onChange={field.onChange}
                  {...register(field.name, {
                    required: true,
                    pattern: /^([RUD]{3}[0-9]{1,13})?$/iu,
                  })}
                  {...field}>
                  {(inputProps) => (
                    <OutlinedInput
                      label='Номер карты'
                      variant='outlined'
                      notched={firstUpdate}
                      fullWidth
                      placeholder='RUD0000012735500'
                      {...inputProps}></OutlinedInput>
                  )}
                </InputMask>
              )}></Controller>
            <FormHelperText>Используйте номер карты водителя для тахографа</FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl error={!!formState.errors.id_autocolon} margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Автоколонна</InputLabel>
            <Controller
              name='id_autocolon'
              control={control}
              render={({ field }) => (
                <Select
                  label='Автоколонна'
                  {...register(field.name)}
                  notched={firstUpdate}
                  value={field.value}
                  onChange={field.onChange}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 200,
                        overflow: 'auto',
                        '&:before': {
                          display: 'none',
                        },
                      },
                    },
                  }}>
                  {sortItemsDivisions.map((division) => {
                    return division.autocolons.map((autocolon, i) => {
                      const style = (theme) => ({
                        marginTop: '30px',
                        position: 'relative',
                        paddingLeft: '20px',
                        '&:before': {
                          content: `"${division.name}"`,
                          display: 'block',
                          position: 'absolute',
                          top: '-30px',
                          fontSize: '12px',
                          width: '100%',
                          left: 0,
                          py: 1,
                          paddingLeft: '10px',
                          bgcolor: alpha(theme.palette.grey.main, 0.12),
                        },
                      });
                      return (
                        <MenuItem
                          key={autocolon.id}
                          value={autocolon.id}
                          sx={
                            i === 0
                              ? style
                              : {
                                  paddingLeft: '20px',
                                }
                          }>
                          {autocolon.name}
                        </MenuItem>
                      );
                    });
                  })}
                </Select>
              )}></Controller>
            <FormHelperText>Выберите автоколонну</FormHelperText>
          </FormControl>
        </Grid>

        <Grid item xs={6}>
          <FormControl error={!!formState.errors.tabel_number} margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Табельный номер</InputLabel>
            <OutlinedInput
              label='Табельный номер'
              variant='outlined'
              notched={firstUpdate}
              fullWidth
              placeholder='Табельный номер'
              {...register('tabel_number', {
                pattern: /^[0-9]{1,20}$/,
                minLength: 1,
                maxLength: 20,
              })}></OutlinedInput>
            <FormHelperText>Используйте табельный номер водителя (только цифры)</FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl error={!!formState.errors.unit} margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Подразделение</InputLabel>
            <OutlinedInput
              label='Подразделение'
              variant='outlined'
              notched={firstUpdate}
              fullWidth
              placeholder='Подразделение'
              {...register('unit', {
                pattern: /^([А-Яа-я\s]{3,255})$/iu,
                required: true,
                minLength: 3,
              })}></OutlinedInput>
            <FormHelperText>Укажите в каком подразделение работает водитель</FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl error={!!formState.errors.external_id} margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Внешний id водителя</InputLabel>
            <OutlinedInput
              label='Внешний id водителя'
              variant='outlined'
              notched={firstUpdate}
              fullWidth
              placeholder='Внешний id водителя'
              {...register('external_id', {
                pattern: /^[0-9]{0,255}$/,
                minLength: 0,
                maxLength: 255,
              })}></OutlinedInput>
            <FormHelperText>Внешний id водителя. Только цифры.</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl error={!!formState.errors.external_groupid} margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Внешний id группы</InputLabel>
            <OutlinedInput
              label='Внешний id группы'
              variant='outlined'
              notched={firstUpdate}
              fullWidth
              placeholder='Внешний id группы'
              {...register('external_groupid', {
                pattern: /^[0-9]{0,255}$/,
                minLength: 0,
                maxLength: 255,
              })}></OutlinedInput>
            <FormHelperText>Внешний id группы. Только цифры.</FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl error={!!formState.errors.drivinglicencenumber} margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Номер лицензии водителя</InputLabel>
            <OutlinedInput
              label='Номер лицензии водителя'
              variant='outlined'
              notched={firstUpdate}
              fullWidth
              placeholder='Номер лицензии водителя'
              {...register('drivinglicencenumber')}></OutlinedInput>
            <FormHelperText>Укажите номер лицензии водителя</FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
    </form>
  );
});

export default DriverForm;
